import { Button, Input, Tooltip } from 'antd';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../Core/Icon';

const SEARCH_DEBOUNCE_MS = 300;

export type FolderPickerSearchMode = 'global' | 'inFolder';

export interface FolderPickerToolbarProps {
  /** Current applied search (from filters). */
  search: string;
  onSearchChange: (search: string) => void;
  /** Root picker uses global `/myfiles/search`; inside a folder uses local list filter. */
  searchMode?: FolderPickerSearchMode;
  /** Global search only: exact match vs like/wildcard (mirrors GlobalSearchField). */
  exactMatch?: boolean;
  onExactMatchChange?: (exactMatch: boolean) => void;
  canCreateFolder: boolean;
  onCreateFolder: () => void;
  className?: string;
}

export function FolderPickerToolbar({
  search,
  onSearchChange,
  searchMode = 'inFolder',
  exactMatch = false,
  onExactMatchChange,
  canCreateFolder,
  onCreateFolder,
  className,
}: FolderPickerToolbarProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(search);
  const debounceRef = useRef<number | null>(null);

  const searchPlaceholder =
    searchMode === 'global'
      ? t('common.globalSearchPlaceholder')
      : t('common.folderPickerSearchInFolder');

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  useEffect(() => {
    return () => {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const flushSearch = (value: string) => {
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setSearchTerm(value);
    onSearchChange(value);
  };

  const handleSearchInput = (value: string) => {
    setSearchTerm(value);
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      onSearchChange(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const showExactMatchToggle = searchMode === 'global' && onExactMatchChange != null;

  // Mirrors GlobalSearchField suffixIcon: like/exact toggle on the right of the field.
  const exactMatchSuffix = showExactMatchToggle ? (
    <div className="relative flex h-5 w-7 items-center justify-center">
      <Tooltip title={exactMatch ? t('common.filterExactMatch') : t('common.filterWholeWord')}>
        <Button
          type="text"
          size="small"
          aria-label={exactMatch ? t('a11y.searchWholeWord') : t('a11y.searchExactMatch')}
          aria-pressed={exactMatch}
          onClick={() => onExactMatchChange(!exactMatch)}
        >
          {exactMatch ? (
            <Icon name="WholeWordIcon" className="h-5 w-5" />
          ) : (
            <Icon name="SquareAsteriskIcon" className="h-5 w-5" />
          )}
        </Button>
      </Tooltip>
    </div>
  ) : undefined;

  return (
    <div className={clsx('flex flex-row items-center gap-2 py-2', className)}>
      <Input
        allowClear
        value={searchTerm}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        // Left lens like GlobalSearchField; ant default radius (theme 5) — not rounded-full.
        // Header Select's [&_.ant-select-selector]:rounded-full! is a no-op on antd 6.
        prefix={<Icon name="SearchIcon" className="text-gray-dark h-4 w-4" />}
        suffix={exactMatchSuffix}
        onChange={(e) => handleSearchInput(e.target.value)}
        onPressEnter={(e) => flushSearch((e.target as HTMLInputElement).value)}
        className="min-w-0 flex-1"
      />
      {canCreateFolder && (
        <Button
          type="primary"
          aria-label={t('common.createFolder')}
          icon={<Icon name="FolderIcon" className="h-5 w-5" />}
          onClick={onCreateFolder}
        >
          {t('common.createFolder')}
        </Button>
      )}
    </div>
  );
}

export default FolderPickerToolbar;
